import { Button, ButtonAsLink } from '@cluey/cluey-components';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useHistory } from 'react-router';

import { ShadowContainer } from '../../../../../components/ShadowContainer';
import { SpecialNeeds } from '../../../../../components/hub/student/SpecialNeeds';
import { ArrowBackIcon, ChevronDownIcon } from '../../../../../icons';
import { FormFlowProps } from '../../../../../types/hubInterfaces';
import { PATH_HUB_HOME } from '../../../../../util/pagePath';
import { useSetPageHeader } from '../../../../../v2/app-shell/usePageHeader';
import { StudentProfileData } from '../../StudentProfile/StudentProfile';

interface Props {
  formFlowProps: FormFlowProps;
}

type HistoryState = {
  enrolStudent?: boolean;
  firstName: string;
  lastName: string;
  yearLevel: string;
  location?: string;
  schoolName?: string;
};

const AddStudentSpecialNeeds = ({ formFlowProps }: Props) => {
  const { jump } = formFlowProps;
  const history = useHistory<HistoryState>();
  const historyState = history.location.state;

  // useFormContext seems to be bugged, the AddStudent component is remounted when navigating to this subroute
  // so formUtils.getValues() returns empty, calling formUtils.setValue will cause another remounting of AddStudent
  const formUtils = useForm<StudentProfileData>({
    defaultValues: {
      ...history.location.state,
      specialLearningNeeds: [],
    },
    resolver: zodResolver(StudentProfileData),
  });

  const { firstName } = formUtils.getValues();

  useSetPageHeader({
    label: 'Students',
    showIcon: false,
    showBackButton: true,
  });

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    if (!firstName) {
      jump(0);
    }
  }, [firstName, jump]);

  const onNext = async () => {
    const formValid = await formUtils.trigger();
    if (formValid) {
      formFlowProps.next({ ...historyState, ...formUtils.getValues() });
    }
  };

  return (
    <div className="md:max-w-4/5 pb-5 md:w-4/5 md:pb-0">
      <ShadowContainer className="flex flex-col gap-6 !rounded-3xl bg-white p-6 md:!p-10">
        <h1 className="font-bold md:text-2xl">Special learning needs</h1>
        <ButtonAsLink onClick={onNext} className="flex items-center gap-1.5">
          <span className="link">Skip this step</span>
          <ArrowBackIcon className="link rotate-[-180deg]" width={20} height={20} />
        </ButtonAsLink>
        <SpecialNeeds student={formUtils.getValues()} formUtils={formUtils} />
        {formUtils.formState.errors?.root && (
          <p className="mt-1 text-xs text-red-5">{formUtils.formState.errors?.root.message}</p>
        )}
        <div className="flex flex-col gap-4 pt-3 md:flex-row md:justify-start">
          <Button onClick={onNext} className="flex h-10 items-center justify-center gap-3 normal-case" size="sm">
            <span>Save and continue</span>
            <ChevronDownIcon className="rotate-[-90deg] text-white" />
          </Button>
          <Button
            onClick={() => {
              history.push(PATH_HUB_HOME);
            }}
            className="flex h-10 items-center justify-center !border normal-case"
            size="sm"
            appearance="reverse"
          >
            <span className="text-base font-medium tracking-wide text-grey-400">Cancel</span>
          </Button>
        </div>
      </ShadowContainer>
    </div>
  );
};

export { AddStudentSpecialNeeds };
